import { Component, input, OnInit, signal, inject } from '@angular/core';

import { Router } from '@angular/router';

import { Canal } from '@libs/entity-lib';
import { TranslationLibModule } from '@libs/translation';
import { MaintenanceApiService, MaintenanceStatus } from '../../services/maintenance-api.service';

import { IonContent, IonSpinner } from '@ionic/angular/standalone';

@Component({
  selector: 'maintenance-page',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss'],
  imports: [IonContent, IonSpinner, TranslationLibModule],
})
export class MaintenanceComponent implements OnInit {
  private maintenanceService = inject(MaintenanceApiService);
  private router = inject(Router);

  appId = input<Canal>();

  timeLeft = signal('00:00:00');
  hasCountdown = signal(false);
  message = signal('No momento, estamos passando por uma manutenção. Já já voltamos!');

  // Propriedade para armazenar o ID do intervalo
  private intervalId!: number;

  ngOnInit(): void {
    const appId = this.appId() ?? Canal.ATHLETE;

    this.maintenanceService.getStatus(appId).subscribe((status) => {
      clearInterval(this.intervalId);
      this.updateMaintenanceStatus(status);
    });
  }

  /**
   * Atualiza o estado de manutenção local
   */
  private updateMaintenanceStatus(status: MaintenanceStatus): void {
    if (status.message) {
      this.message.set(status.message);
    }
    if (status.expiresAt) {
      this.startCountdown(status.expiresAt);
    } else {
      this.hasCountdown.set(false);
    }
  }

  /**
   * Inicia o cronômetro decrescente com base no timestamp do campo time
   * @param time Timestamp ISO 8601 representando o fim da manutenção (em UTC)
   */
  private startCountdown(expiresAt: string): void {
    // Obter o horário atual em UTC
    const nowUTC = new Date().getTime(); // Horário atual em milissegundos UTC
    const expiresAtUTC = new Date(expiresAt).getTime(); // Converter `expiresAt` para milissegundos UTC

    // Calcular o tempo restante
    let remainingMilliseconds = expiresAtUTC - nowUTC;

    // Se o tempo já expirou
    if (remainingMilliseconds <= 0) {
      this.timeLeft.set('00:00:00');
      this.hasCountdown.set(false);
      this.router.navigate(['/']);
      return;
    }

    this.hasCountdown.set(true);
    this.updateTimeLeft(remainingMilliseconds);

    // Configurar o intervalo para decrementar o tempo
    this.intervalId = setInterval(() => {
      remainingMilliseconds -= 1000; // Reduz 1 segundo em milissegundos

      if (remainingMilliseconds <= 0) {
        this.timeLeft.set('00:00:00');
        this.hasCountdown.set(false);
        this.router.navigate(['/']);
        clearInterval(this.intervalId); // Para o cronômetro ao atingir 0
      } else {
        this.updateTimeLeft(remainingMilliseconds);
      }
    }, 1000) as unknown as number;
  }

  /**
   * Atualiza o tempo restante no formato HH:MM:SS
   * @param remainingMilliseconds Tempo restante em milissegundos
   */
  private updateTimeLeft(remainingMilliseconds: number): void {
    const hours = Math.floor(remainingMilliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remainingMilliseconds % (1000 * 60)) / 1000);

    this.timeLeft.set(
      `${this.padNumber(hours)}:${this.padNumber(minutes)}:${this.padNumber(seconds)}`
    );
  }

  /**
   * Adiciona um zero à esquerda em números menores que 10
   */
  private padNumber(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}
